import React, { useEffect, useRef } from 'react';
import { Typography, Paper, Grid, Box, makeStyles, Container } from '@material-ui/core';
import useCommonStyles from '../styles';
import PrmTitleRow from '../PrmTitleRow';
import SmallBarChart from '../SplitProgressBarChart/SmallBarChart';
import PieChart from 'components/@common/Charts/PieChart';
import ArrowForward from '@material-ui/icons/ArrowForward';
import { PrmOrganizationCurriculumProgress } from 'types/schema-generated-types';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import { capitalize } from 'lodash';
import { ReportEntityType } from 'components/ReportsModule/types';
import { buildPieChartOptions } from './engagement-util';
import MaybeShowErrorMessageWithLoading from 'components/ReportsModule/components/common/MaybeShowErrorMessageWithLoading';

type Props = {
  setList: any[];
  organizationCurriculumProgress?: PrmOrganizationCurriculumProgress[];
  setId?: (id?: any) => void;
  selectedId?: number;
  loading?: boolean;
};

const Engagement: React.FC<Props> = ({
  setList,
  organizationCurriculumProgress,
  setId,
  selectedId,
  loading
}) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const [{ entityType }, setQuery] = useReportQueryParams();
  const containerRef = useRef<HTMLElement>();
  const getRedirectEntityType = () => {
    if (entityType === ReportEntityType.DISTRICT) return ReportEntityType.SCHOOL;
    return ReportEntityType.CLASSROOM;
  };

  const buildChartValues = (value: PrmOrganizationCurriculumProgress) => {
    return [
      value.averageLessonCompletedCount,
      value.averageLessonAssignedCount - value.averageLessonCompletedCount,
      value.totalLessonCount - value.averageLessonAssignedCount
    ];
  };

  const handleClick = (id: any) => {
    setQuery({ entityType: getRedirectEntityType(), entityId: id });
  };

  // sets the first item as initial
  useEffect(() => {
    if (!setList?.[0]?.id) return;
    setId?.(setList?.[0]?.id);
  }, [setList, setId]);

  return (
    <>
      <Typography variant="h3" style={{ marginBottom: 6 }}>
        Engagement
      </Typography>
      <Paper className={commonClasses.container} ref={containerRef}>
        <Container maxWidth="xl">
          <PrmTitleRow
            title="Schools Overview"
            setList={setList}
            containerProps={{ marginBottom: 4 }}
            handleClick={setId}
            selected={selectedId}
          />
          <Grid container spacing={2} wrap="wrap">
            <MaybeShowErrorMessageWithLoading
              shouldRender={!!organizationCurriculumProgress?.length}
              loading={loading!}
              width={`100%`}
            >
              {organizationCurriculumProgress?.map(x => (
                <Grid item xs={6} wrap="wrap" style={{ maxWidth: 'unset' }}>
                  <Box width={520}>
                    <Box
                      style={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'space-between',
                        marginBottom: 24
                      }}
                    >
                      <Typography className={classes.link}>{x.organization?.name}</Typography>
                      <Typography
                        component="a"
                        onClick={() => handleClick(x?.organization?.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#006DB8',
                          fontSize: 12,
                          fontWeight: 450,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        <ArrowForward fontSize="small" style={{ color: '#006DB8', fontSize: 12 }} />
                        <span>View {capitalize(getRedirectEntityType())} Report</span>
                      </Typography>
                    </Box>
                    <Paper variant="outlined" className={classes.card}>
                      <Box className={classes.chartContainer}>
                        <PieChart
                          type="donut"
                          series={buildChartValues(x)}
                          height={180}
                          options={buildPieChartOptions(x)}
                        />

                        <Typography className={classes.chartTitle}>
                          Total Lessons Complete
                        </Typography>
                      </Box>

                      {x.overallCompletionStats.metSkillPercentage > 0 && (
                        <SmallBarChart
                          completedPercent={x.overallCompletionStats.metSkillPercentage}
                          supportedPercent={x.overallCompletionStats.metSkillWithSupportPercentage}
                        />
                      )}
                    </Paper>
                  </Box>
                </Grid>
              ))}
            </MaybeShowErrorMessageWithLoading>
          </Grid>
        </Container>
      </Paper>
    </>
  );
};

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flex: 1,
    height: 'auto',

    borderRadius: 12,
    padding: 10,
    flexWrap: 'wrap'
  },
  chartContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '40%',
    flexDirection: 'column'
  },
  chartTitle: {
    width: '50%',
    textAlign: 'center',
    fontWeight: 450,
    fontSize: 16
  },
  link: {
    fontWeight: 450,
    fontSize: 22
  }
}));
export default Engagement;
